'use client'

import Footer from '@/components/Footer'
import WhitelabelCTA from '@/components/services/WhitelabelCTA'
import WorkizHero from '@/components/workiz/WorkizHero'
import WorkizPlans from '@/components/workiz/WorkizPlans'
import WorkizStages from '@/components/workiz/WorkizStages'
import {
  WORKIZ_ADDONS,
  WORKIZ_BUILD_PILLARS,
  WORKIZ_FAQS,
  WORKIZ_SYMPTOMS,
} from '@/lib/workiz-reporting-data'
import { CardCheck, WarnIcon } from '@/components/zoho/icons'

const ENGAGEMENT_STANDARDS = [
  'Read-only against your Workiz data',
  'Credentials stored encrypted',
  'Built in your Google Workspace',
  'You own it - nothing breaks if we part ways',
  'No password sharing',
  'No additional paid seats',
  'US-based, no subcontractors, no offshore',
  'MSA and SOW on every engagement',
] as const

export default function WorkizPage() {
  return (
    <div className="bg-white text-slate-900">
      <div className="bg-white lg:px-16">
        <WorkizHero />
      </div>

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">
          The problem with the numbers you have now
        </p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          Most Workiz shops end up in the same spreadsheet
        </h2>
        <p className="mt-5 text-[16.5px] leading-[1.65] text-slate-600">
          Somebody exports the jobs report and the leads report each month, pastes them into a spreadsheet,
          types in ad spend from four different tabs, and builds a picture that&apos;s out of date before
          it&apos;s finished.
        </p>
        <p className="mt-4 text-[16.5px] leading-[1.65] text-slate-600">
          Then the numbers stop agreeing with each other.
        </p>
        <ul className="mt-8 space-y-3">
          {WORKIZ_SYMPTOMS.map((item) => (
            <li key={item.title} className="flex items-start gap-3 text-[16px] leading-[1.5] text-slate-700">
              <WarnIcon />
              <span>
                <span className="font-semibold text-[#0a2b5c]">{item.title}</span>
                <span className="text-slate-600">, {item.detail}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-[16px] leading-[1.65] text-slate-500">
          None of it is anyone&apos;s fault. It&apos;s what happens when a spreadsheet built for one month
          gets used for two years - and when the CRM and the ad platforms were never designed to talk to
          each other.
        </p>
      </section>

      <section className="bg-[#f7f9fc] px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">What I build</p>
          <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
            Spend and revenue, on the same dates, against the same sources
          </h2>
          <div className="mt-10 space-y-10">
            {WORKIZ_BUILD_PILLARS.map((pillar) => (
              <div key={pillar.title}>
                <h3 className="text-[22px] font-bold tracking-[-0.03em] text-[#0a2b5c]">{pillar.title}</h3>
                {'body' in pillar && pillar.body && (
                  <p className="mt-3 text-[16px] leading-[1.65] text-slate-600">{pillar.body}</p>
                )}
                {'note' in pillar && pillar.note && (
                  <p className="mt-3 text-[15px] leading-[1.6] text-slate-500">{pillar.note}</p>
                )}
                {'items' in pillar && pillar.items && (
                  <ul className="mt-4 space-y-3">
                    {pillar.items.map((item) => (
                      <li key={item.label} className="flex items-start gap-2.5 text-[16px] leading-[1.5] text-slate-700">
                        <CardCheck />
                        <span>
                          <span className="font-semibold text-[#0a2b5c]">{item.label}</span>
                          {item.detail ? <span className="text-slate-500"> - {item.detail}</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="plan" className="scroll-mt-24 mx-auto max-w-4xl px-4 pb-6 pt-16 text-center sm:px-6 sm:pt-20 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">How it works</p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          Audit. Build. Ongoing.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[16.5px] leading-[1.6] text-slate-600">
          Fixed-scope review first. Then automated sync, connected ad spend, and a dashboard you can trust.
          Optional monthly management to keep it accurate.
        </p>
      </section>

      <WorkizStages />

      <WorkizPlans />

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">{WORKIZ_ADDONS.title}</p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          Layer on what the reporting needs next
        </h2>
        <p className="mt-4 text-[15.5px] leading-[1.6] text-slate-500">{WORKIZ_ADDONS.note}</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {WORKIZ_ADDONS.items.map((addon) => (
            <article key={addon.name} className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-[0_12px_40px_rgba(15,35,70,0.06)]">
              <h3 className="text-[17px] font-bold text-[#0a2b5c]">{addon.name}</h3>
              <p className="mt-2 text-[15px] leading-[1.55] text-slate-600">{addon.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="relative isolate bg-[#071631] pt-10 pb-0">
        <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden={true}>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/images/homepage/cta.avif')" }}
          />
          <div className="absolute inset-0 bg-[#2b2c48]/25" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="pb-16">
            <div className="mb-10 text-center">
              <h4 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-100/60">
                Engagement standards
              </h4>
              <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                {ENGAGEMENT_STANDARDS.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-[13px] font-medium leading-[1.45] text-blue-50/85"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <WhitelabelCTA
              embedded
              hideStats
              heading="Get a Quote"
              buttonText="Get a Quote"
              formSource="workiz-reporting-lp"
              serviceName="Workiz Marketing Reporting"
              focusAreaPlaceholder="Workiz, 8 techs, Google Ads + Facebook + Thumbtack. Spreadsheet reporting, sources don't match, not sure which channel is actually making money."
            />
          </div>
        </div>
      </section>

      <section id="faq" className="scroll-mt-24 mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">Straight answers</p>
        <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
          Still deciding
        </h2>
        <div className="mt-8 divide-y divide-slate-200 border-t border-slate-200">
          {WORKIZ_FAQS.map((faq) => (
            <div key={faq.question} className="py-5">
              <h3 className="text-[17px] font-semibold text-[#0a2b5c]">{faq.question}</h3>
              <p className="mt-3 text-[15px] leading-[1.6] text-slate-600">{faq.answer}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href="#get-quote"
            className="inline-flex h-14 items-center justify-center rounded-full bg-[#13479c] px-9 text-[16px] font-bold text-white shadow-[0_12px_28px_rgba(19,71,156,0.26)] transition-colors hover:bg-[#0f3a7a]"
          >
            Get In Touch
          </a>
        </div>
      </section>

      <Footer />
    </div>
  )
}
